import React, { useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Plane, TrendingUp, Database } from 'lucide-react';

export default function About() {
  useEffect(() => {
    const theme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  return (
    <div className="about-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto+Slab:wght@900&family=Montserrat:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

        :root[data-theme="dark"] {
          --bg-primary: var(--bg-app);
          --bg-secondary: #141918;
          --text-primary: #F4F1E8;
          --text-secondary: #C2BDB0;
          --text-muted: #8B8575;
          --accent-primary: #D97642;
          --accent-green: #5FD4A0;
          --border-primary: rgba(212, 196, 168, 0.15);
        }

        :root[data-theme="light"] {
          --bg-primary: #F8F6F1;
          --bg-secondary: #FFFFFF;
          --text-primary: #1A1612;
          --text-secondary: #4A453C;
          --text-muted: #6B6558;
          --accent-primary: #D97642;
          --accent-green: #3BA876;
          --border-primary: rgba(26, 22, 18, 0.15);
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .about-page {
          min-height: 100vh;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .about-content {
          flex: 1;
          max-width: 800px;
          margin: 0 auto;
          padding: 3rem 1.5rem;
          width: 100%;
        }

        .about-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }

        .about-subtitle {
          font-size: 1.125rem;
          color: var(--text-secondary);
          margin-bottom: 3rem;
        }

        .about-section {
          margin-bottom: 3rem;
        }

        .section-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.5rem;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .section-text {
          font-size: 1rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 1rem;
        }

        .highlight-box {
          background: linear-gradient(135deg, rgba(217, 118, 66, 0.08), rgba(95, 212, 160, 0.08));
          border-left: 4px solid var(--accent-primary);
          padding: 1.5rem;
          margin: 2rem 0;
          border-radius: 0.5rem;
        }

        .highlight-text {
          font-size: 1.125rem;
          font-weight: 500;
          line-height: 1.7;
          color: var(--text-primary);
        }

        @media (max-width: 768px) {
          .about-content {
            padding: 2rem 1.5rem;
          }

          .about-title {
            font-size: 2rem;
          }

          .section-title {
            font-size: 1.25rem;
          }
        }
      `}</style>

      <Navigation />

      <div className="about-content">
        <h1 className="about-title">About MIZAR</h1>
        <p className="about-subtitle">
          Flight price prediction infrastructure for online travel sellers
        </p>

        <div className="about-section">
          <h2 className="section-title">
            <Plane size={24} />
            From TravelTxter to MIZAR
          </h2>
          <p className="section-text">
            MIZAR grew out of TravelTxter, a consumer travel project built to monitor flight prices and surface better booking opportunities.
          </p>
          <p className="section-text">
            As the data pipeline matured, a larger opportunity became clear. The same price movement intelligence that helped explain fare changes to travellers could also help online travel sellers understand when uncertainty may be delaying booking decisions.
          </p>
        </div>

        <div className="highlight-box">
          <p className="highlight-text">
            MIZAR exists to answer one commercial question: when a traveller is hesitating, is the fare likely to become more expensive if they wait?
          </p>
        </div>

        <div className="about-section">
          <h2 className="section-title">
            <TrendingUp size={24} />
            Built on Verified Fare Movement
          </h2>
          <p className="section-text">
            MIZAR analyses route, fare, timing, and market conditions to produce a RegretRisk score: a calibrated probability that a fare will rise by 10 per cent or more within seven days.
          </p>
          <p className="section-text">
            The production model is continuously evaluated against observed fare outcomes through an independent verification process. Model lineage and outcome tracking are maintained to support governance, auditability, and future model improvement.
          </p>
        </div>

        <div className="about-section">
          <h2 className="section-title">
            <Database size={24} />
            Designed for Online Travel Sellers
          </h2>
          <p className="section-text">
            MIZAR is delivered as a REST API. A booking platform sends route and fare context to the signal endpoint and receives a RegretRisk score, recommendation, confidence band, and model version in response.
          </p>
          <p className="section-text">
            During Shadow Mode, the API can run alongside an existing booking flow without changing pricing, checkout, or customer experience. This allows travel sellers to evaluate signal quality and commercial relevance before deciding whether to introduce customer-facing interventions.
          </p>
        </div>

        <div className="highlight-box">
          <p className="highlight-text">
            MIZAR is not a checkout replacement or pricing engine. It is a decision layer designed to help travel sellers identify where fare uncertainty may be affecting booking behaviour.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}